import React, { useEffect, useMemo, useRef, useState } from "react";
import { RADAR_SITES_CATALOG } from "../data/radar-sites";
import "./SiteSelector.css";

function formatAge(seconds) {
  if (seconds == null || seconds < 0) return null;
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}

// Compute age in whole seconds from an ISO-8601 timestamp string.
// Returns null when the string is absent or unparseable.
function ageSeconds(isoStr, now) {
  if (!isoStr) return null;
  const t = new Date(isoStr).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now - t) / 1000));
}

// Map elapsed seconds → health band.
//   live     < 5 min
//   delayed  5–15 min
//   stale    15–60 min
//   offline  > 60 min
const BAND_LABELS = { live: "Live", delayed: "Delayed", stale: "Stale", offline: "Offline" };

function healthBand(ageSec) {
  if (ageSec == null) return "unknown";
  if (ageSec < 300)  return "live";
  if (ageSec < 900)  return "delayed";
  if (ageSec < 3600) return "stale";
  return "offline";
}

export default function SiteSelector({
  siteHealthDetails = {},
  activeSite,
  onSelectSite,
  onClose,
}) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  // Tick every 60 s so "Xm ago" labels advance without waiting for an API poll.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const sites = useMemo(() => {
    const q = query.trim().toLowerCase();

    const allSites = RADAR_SITES_CATALOG.filter((s) => s.active).map((s) => {
      const detail = siteHealthDetails[s.id];
      return {
        id: s.id,
        name: detail?.name ?? s.name,
        lat: detail?.lat ?? s.lat,
        lon: detail?.lon ?? s.lon,
        hasData: s.hasData,
        siteStatus: detail?.site_status ?? "unknown",
        health: detail?.health ?? "unknown",
        vcp: detail?.vcp ?? null,
        // Use absolute ISO timestamp so age advances in real time.
        // Falls back to the pre-computed integer for backwards compatibility.
        lastScanTime: detail?.last_received_scan_time ?? null,
        secondsSince: detail?.seconds_since_last_scan ?? null,
        isWatched: !!detail,
      };
    });

    const filtered = q
      ? allSites.filter(
          (s) =>
            s.id.toLowerCase().includes(q) ||
            (s.name && s.name.toLowerCase().includes(q))
        )
      : allSites;

    const statusOrder = { healthy: 0, stale: 1, degraded: 2, down: 3, unknown: 4 };
    filtered.sort((a, b) => {
      if (a.isWatched !== b.isWatched) return a.isWatched ? -1 : 1;
      if (a.isWatched && b.isWatched) {
        const sa = statusOrder[a.siteStatus] ?? 4;
        const sb = statusOrder[b.siteStatus] ?? 4;
        if (sa !== sb) return sa - sb;
      }
      return a.id.localeCompare(b.id);
    });

    return filtered;
  }, [query, siteHealthDetails]);

  return (
    <div className="site-selector-overlay">
      <div className="site-selector-search">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search sites..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button className="site-selector-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      <div className="site-selector-list">
        {sites.length === 0 && (
          <div className="site-selector-empty">No sites match</div>
        )}
        {sites.map((site) => {
          // Prefer dynamically-computed age from absolute timestamp; fall back to
          // the integer snapshot so sites without a timestamp still show something.
          const ageSec = site.lastScanTime
            ? ageSeconds(site.lastScanTime, now)
            : site.secondsSince;
          const band = healthBand(ageSec);

          return (
            <div
              key={site.id}
              className={`site-selector-item site-selector-item--${band}${site.id === activeSite ? " active" : ""}`}
              onClick={() => onSelectSite?.(site.id)}
            >
              <div className={`site-selector-dot ${site.siteStatus}`} />
              <div className="site-selector-info">
                <div className="site-selector-row1">
                  <span className="site-selector-id">{site.id}</span>
                  <span className="site-selector-name">{site.name}</span>
                </div>
                {(site.vcp != null || ageSec != null) && (
                  <div className="site-selector-row2">
                    {site.vcp != null && (
                      <span className="site-selector-vcp">VCP {site.vcp}</span>
                    )}
                    {band !== "unknown" && (
                      <span className={`site-selector-badge site-selector-badge--${band}`}>
                        {BAND_LABELS[band]}
                      </span>
                    )}
                    {ageSec != null && (
                      <span className={`site-selector-time site-selector-time--${band}`}>
                        {formatAge(ageSec)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {site.id === activeSite && (
                <span className="site-selector-active-mark">✓</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
